import { useMemo } from "react";

// Convert HEX to RGB
const hexToRgb = (hex: String) => {
  hex = hex.replace("#", "");
  return [
    parseInt(hex.substring(0, 2), 16) / 255,
    parseInt(hex.substring(2, 4), 16) / 255,
    parseInt(hex.substring(4, 6), 16) / 255,
  ];
};

// Convert RGB to XYZ
const rgbToXyz = (rgb: Number[]) => {
  const transform = (c: Number) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

  const r = transform(rgb[0]) * 100;
  const g = transform(rgb[1]) * 100;
  const b = transform(rgb[2]) * 100;

  return [
    r * 0.4124564 + g * 0.3575761 + b * 0.1804375,
    r * 0.2126729 + g * 0.7151522 + b * 0.0721750,
    r * 0.0193339 + g * 0.1191920 + b * 0.9503041,
  ];
};

// Convert XYZ to LAB
const xyzToLab = (xyz: Number[]) => {
  const transform = (c: Number) =>
    c > 0.008856 ? Math.pow(c, 1 / 3) : (7.787 * c) + (16 / 116);

  const x = transform(xyz[0] / 95.047);
  const y = transform(xyz[1] / 100.000);
  const z = transform(xyz[2] / 108.883);

  return [(116 * y) - 16, 500 * (x - y), 200 * (y - z)];
};

// Compute CIEDE2000 color difference
const cie2000 = (lab1: number[], lab2: number[]) => {
  const [L1, a1, b1] = lab1;
  const [L2, a2, b2] = lab2;

  const avgL = (L1 + L2) / 2;
  const C1 = Math.sqrt(a1 ** 2 + b1 ** 2);
  const C2 = Math.sqrt(a2 ** 2 + b2 ** 2);
  const avgC = (C1 + C2) / 2;

  const G = 0.5 * (1 - Math.sqrt(avgC ** 7 / (avgC ** 7 + Math.pow(25, 7))));
  const a1Prime = a1 * (1 + G);
  const a2Prime = a2 * (1 + G);

  const C1Prime = Math.sqrt(a1Prime ** 2 + b1 ** 2);
  const C2Prime = Math.sqrt(a2Prime ** 2 + b2 ** 2);
  const avgCPrime = (C1Prime + C2Prime) / 2;

  const h1Prime = Math.atan2(b1, a1Prime) * (180 / Math.PI);
  const h2Prime = Math.atan2(b2, a2Prime) * (180 / Math.PI);
  const h1 = h1Prime < 0 ? h1Prime + 360 : h1Prime;
  const h2 = h2Prime < 0 ? h2Prime + 360 : h2Prime;

  const deltaL = L2 - L1;
  const deltaC = C2Prime - C1Prime;
  const deltaH =
    C1Prime * C2Prime === 0
      ? 0
      : h2 - h1 > 180
        ? h2 - h1 - 360
        : h2 - h1 < -180
          ? h2 - h1 + 360
          : h2 - h1;
  const deltaHPrime = 2 * Math.sqrt(C1Prime * C2Prime) * Math.sin((deltaH * Math.PI) / 360);

  const avgH =
    C1Prime * C2Prime === 0
      ? h1 + h2
      : Math.abs(h1 - h2) > 180
        ? (h1 + h2 + 360) / 2
        : (h1 + h2) / 2;

  const T =
    1 -
    0.17 * Math.cos(((avgH - 30) * Math.PI) / 180) +
    0.24 * Math.cos((2 * avgH * Math.PI) / 180) +
    0.32 * Math.cos(((3 * avgH + 6) * Math.PI) / 180) -
    0.20 * Math.cos(((4 * avgH - 63) * Math.PI) / 180);

  const SL = 1 + (0.015 * Math.pow(avgL - 50, 2)) / Math.sqrt(20 + Math.pow(avgL - 50, 2));
  const SC = 1 + 0.045 * avgCPrime;
  const SH = 1 + 0.015 * avgCPrime * T;

  const deltaTheta = 30 * Math.exp(-Math.pow((avgH - 275) / 25, 2)); // Fixed Here
  const RC = 2 * Math.sqrt(avgCPrime ** 7 / (avgCPrime ** 7 + Math.pow(25, 7)));
  const RT = -RC * Math.sin((2 * deltaTheta * Math.PI) / 180);

  return Math.sqrt(
    Math.pow(deltaL / SL, 2) +
    Math.pow(deltaC / SC, 2) +
    Math.pow(deltaHPrime / SH, 2) +
    RT * (deltaC / SC) * (deltaHPrime / SH)
  );
};


// Custom Hook
export const useColorDifference = (color1: String, color2: String) => {
  return useMemo(() => {
    if(color1 === undefined || color2 === undefined) {
      return null
    } else {
      const lab1 = xyzToLab(rgbToXyz(hexToRgb(color1)));
      const lab2 = xyzToLab(rgbToXyz(hexToRgb(color2)));

      return cie2000(lab1, lab2).toFixed(2);
    }
  }, [color1, color2]);
};
